import { useQuery } from '@tanstack/react-query'
import type { ConversationDetailDTO } from '@sac/contracts'
import { api } from '../../lib/api'
import { qk } from '../../lib/query'
import { useUi } from '../../state/UiContext'
import { CustomerAvatar } from '../CustomerAvatar'
import { ChannelMark, Icon, channelName } from '../icons'
import { OrderCard } from './OrderCard'

/**
 * The customer panel: who they are, then what they bought.
 *
 * Orders used to live behind a separate tab, which meant the two questions an agent always asks
 * together — "who is this?" and "what did they order?" — cost a click to connect. They are one
 * scroll now, ordered newest first, because the order being asked about is almost always the last
 * one placed.
 */
export function CustomerTab({ conv }: { conv: ConversationDetailDTO }) {
  const { state, dispatch } = useUi()
  const { data: customer } = useQuery({
    queryKey: qk.customer(conv.customerId),
    queryFn: () => api.customer(conv.customerId),
  })
  const { data: orders } = useQuery({
    queryKey: qk.customerOrders(conv.customerId),
    queryFn: () => api.customerOrders(conv.customerId),
  })
  if (!customer) return null

  // Newest first. The order this conversation is about (if the channel told us) is pulled to the
  // top regardless of date — it is the one the agent is looking for.
  const sorted = [...(orders ?? [])].sort(
    (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
  )
  const primary = conv.primaryOrderId ? sorted.find((o) => o.id === conv.primaryOrderId) : undefined
  const rest = primary ? sorted.filter((o) => o.id !== primary.id) : sorted

  return (
    <>
      <div className="cust-top">
        <CustomerAvatar seed={conv.customerId} color={customer.color} name={customer.displayName} size={34} />
        <div>
          <div className="cust-name">{customer.displayName}</div>
          <div className="cust-sub">Cliente desde {customer.customerSince ?? '—'}</div>
        </div>
      </div>
      <div className="blk">
        {customer.phone && (
          <div className="kv">
            <Icon name="phone" />
            {customer.phone}
            <span className="cp">copiar</span>
          </div>
        )}
        {customer.email && (
          <div className="kv">
            <Icon name="mail" />
            {customer.email}
            <span className="cp">copiar</span>
          </div>
        )}
        {customer.city && (
          <div className="kv">
            <Icon name="pin" />
            {customer.city}
          </div>
        )}
      </div>
      <div className="blk">
        <div className="blk-h">Canais</div>
        {customer.identities
          .filter((i) => i.verified)
          .map((i) => (
            <div className="chan-line" key={i.id}>
              <ChannelMark channel={i.channelType} size={18} />
              {channelName(i.channelType)}
              <span className="who">{i.username ?? i.phone ?? i.email ?? i.externalUserId}</span>
            </div>
          ))}
        {customer.identities.some((i) => !i.verified && !i.rejectedAt) && (
          <>
            {customer.identities
              .filter((i) => !i.verified && !i.rejectedAt)
              .map((i) => (
                <div className="chan-line" style={{ opacity: 0.45 }} key={i.id}>
                  <ChannelMark channel={i.channelType} size={18} />
                  {channelName(i.channelType)}
                  <span className="who">{i.username ?? i.phone ?? i.email}</span>
                </div>
              ))}
            <div style={{ fontSize: 11.5, color: 'var(--text-3)', marginTop: 5 }}>
              Identidade aguardando confirmação na conversa.
            </div>
          </>
        )}
      </div>
      <button className="more-btn" onClick={() => dispatch({ type: 'toggleMoreCust' })}>
        {state.moreCust ? 'Ocultar detalhes' : 'Mais detalhes'}
        <Icon name="chev" size={11} />
      </button>
      {state.moreCust && (
        <div className="blk" style={{ marginTop: 10 }}>
          <div className="dl">
            <span className="k">Pedidos</span>
            <span className="v">{customer.orderCount}</span>
          </div>
          <div className="dl">
            <span className="k">Total gasto</span>
            <span className="v">R$ {customer.totalSpent.toFixed(2).replace('.', ',')}</span>
          </div>
          <div className="dl">
            <span className="k">Conversas</span>
            <span className="v">{customer.conversationCount}</span>
          </div>
          <div className="dl">
            <span className="k">Segmento</span>
            <span className="v">{customer.orderCount > 1 ? 'Recorrente' : 'Primeira compra'}</span>
          </div>
          {customer.notes && (
            <div
              style={{
                marginTop: 9,
                fontSize: 12.5,
                color: 'var(--text-2)',
                background: 'var(--surface-2)',
                border: '1px solid var(--border)',
                borderRadius: 9,
                padding: 9,
              }}
            >
              {customer.notes}
            </div>
          )}
        </div>
      )}

      <div className="ctx-div" />

      {primary && (
        <>
          <div className="blk-h">Pedido desta conversa</div>
          <OrderCard order={primary} primary />
        </>
      )}
      <div className="blk-h" style={{ marginTop: primary ? 16 : 0 }}>
        {primary ? 'Outros pedidos' : 'Pedidos'}
        {rest.length > 0 && <span className="cnt">{rest.length}</span>}
      </div>
      {rest.length === 0 && !primary ? (
        <div className="empty" style={{ padding: '18px 10px' }}>
          Nenhum pedido associado a este cliente.
        </div>
      ) : (
        rest.map((o) => <OrderCard key={o.id} order={o} primary={false} />)
      )}
    </>
  )
}
